import { create } from 'zustand';

/** An optional follow-up action alongside Dismiss — e.g. the base.css comment's own "Task 17
 *  stage-up celebration: 'Open wardrobe' link + 'Dismiss'" pairing (`.toast-actions`, styled but
 *  never wired to a real component until this task). `onClick` fires and the toast dismisses
 *  itself immediately after — a toast is never left open waiting on the action's own result. */
export type ToastAction = { label: string; onClick: () => void };

export type ToastMessage = { id: number; text: string; action?: ToastAction; shownAt: number };

type ToastState = {
  /** SCREENS.md "Global additions": "one at a time" — a new `show()` replaces whatever toast is
   *  currently visible rather than queuing behind it. That holds even mid-exit. */
  toast: ToastMessage | null;
  /** True between `beginDismiss()` and `clear()`. The toast is still rendered during this
   *  window so its leave animation has something to run on — the whole reason dismissal is two
   *  steps rather than one. */
  exiting: boolean;
  show: (text: string, action?: ToastAction) => void;
  /** Start the leave animation. `Toast.tsx` calls `clear()` on animationend. */
  beginDismiss: () => void;
  /** Remove immediately, skipping the animation — used by swipe-dismiss, where the finger has
   *  already outrun any exit we could play. */
  clear: () => void;
  /** Kept as an alias for `beginDismiss` so the ~25 existing call sites need no change. */
  dismiss: () => void;
};

let nextId = 1;

export const useToastStore = create<ToastState>((set, get) => ({
  toast: null,
  exiting: false,
  show: (text, action) => set({ toast: { id: nextId++, text, action, shownAt: Date.now() }, exiting: false }),
  beginDismiss: () => set((s) => (s.toast ? { exiting: true } : s)),
  clear: () => set({ toast: null, exiting: false }),
  // Ledger item (final whole-branch review): this used to be its own copy of beginDismiss's own
  // lambda rather than a delegate to it — the two were documented as aliases and ~25 call sites
  // depend on that equivalence, but a future edit to one would silently stop propagating to the
  // other. Delegating through `get()` makes that impossible by construction.
  dismiss: () => get().beginDismiss(),
}));

/**
 * Non-hook convenience for call sites outside a component's render (a mutation's `onSuccess`/
 * `onError` callback, e.g. `useSendGift`'s consumer showing "Gift sent to {handle}") — reaches
 * the store's own imperative `getState()` API, same pattern `pushPrompt.ts`'s standalone
 * `recordAsk()` uses around `usePushStore`.
 */
export function showToast(text: string, action?: ToastAction): void {
  useToastStore.getState().show(text, action);
}
